/**
 * Zod schemas for the authentication surface (docs/APP_PLAN.md §12 M2,
 * APP_SPEC §8–§12, TECH_STACK §24, §36).
 *
 * These are the single source of truth for the *shape* of auth requests.
 * `apps/api` uses them to validate every untrusted input server-side
 * (authoritative); `apps/web` (Milestone 2b) imports the same schemas for
 * client-side form validation via React Hook Form, so the two can never
 * drift out of sync.
 *
 * Password policy (docs/APP_PLAN.md §9.1): minimum 12 characters, maximum
 * 128 (bounded to prevent hashing-cost DoS from pathologically long
 * inputs), no composition rules.
 */
import { normalizeUsername } from '@perplexicon/shared';
import { USERNAME_CONSTRAINTS } from '@perplexicon/config';
import { z } from 'zod';

/** Mirrors the `users_username_normalized_charset_check` database CHECK constraint exactly. */
const USERNAME_NORMALIZED_PATTERN = /^[a-z0-9][a-z0-9_-]{2,23}$/;

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/**
 * Validates the *display* username a user types in. Length bounds mirror
 * `users_username_display_length_check`; the charset check is applied to
 * the normalized form so `HelloIAmUser` and `hello-i-am-user` are judged by
 * the same rule the database ultimately enforces.
 */
export const usernameSchema = z
  .string()
  .trim()
  .min(
    USERNAME_CONSTRAINTS.minLength,
    `Username must be at least ${USERNAME_CONSTRAINTS.minLength} characters.`,
  )
  .max(
    USERNAME_CONSTRAINTS.maxLength,
    `Username must be at most ${USERNAME_CONSTRAINTS.maxLength} characters.`,
  )
  .refine((value) => USERNAME_NORMALIZED_PATTERN.test(normalizeUsername(value)), {
    message:
      'Username may only contain letters, numbers, underscores, and hyphens, and must start with a letter or number.',
  });

export const emailSchema = z
  .string()
  .trim()
  .min(3, 'Email is required.')
  .max(254, 'Email is too long.')
  .email('Enter a valid email address.');

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`)
  .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`);

// Plain `z.object()` (not `.strict()`): per docs/APP_PLAN.md §9.3, "unknown
// object keys are stripped, not passed through" — Zod's default object
// behavior silently drops unrecognized keys rather than rejecting the
// request for carrying them.

export const registerRequestSchema = z.object({
  username: usernameSchema,
  email: emailSchema,
  password: passwordSchema,
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const loginRequestSchema = z.object({
  username: z.string().trim().min(1, 'Username is required.').max(254),
  password: z.string().min(1, 'Password is required.').max(PASSWORD_MAX_LENGTH),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const passwordResetRequestSchema = z.object({
  email: emailSchema,
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetCompleteSchema = z.object({
  token: z.string().min(1, 'Reset token is required.').max(512),
  newPassword: passwordSchema,
});
export type PasswordResetComplete = z.infer<typeof passwordResetCompleteSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required.').max(PASSWORD_MAX_LENGTH),
  newPassword: passwordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;
